import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Warning, MapPin, Phone, FirstAid } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { countryName } from "@/lib/countries";

const risk = (v) => String(v || "none").replace(/\b\w/g, (c) => c.toUpperCase());

export default function SafetyPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/safety/public/${token}`)
      .then((res) => setData(res.data))
      .catch(() => setError("That safety page is not shared."));
  }, [token]);

  if (error) return <div data-testid="public-safety-error" className="p-10 text-center text-sm text-slate-500">{error}</div>;
  if (!data) return <div className="p-10 text-center text-sm text-slate-500">Loading…</div>;

  const s = data.site;
  return (
    <div data-testid="public-safety-page" className="mx-auto max-w-3xl p-5 sm:p-10">
      <p className="overline text-slate-500">Unity &amp; Utility · Site safety information</p>
      <h1 data-testid="public-safety-title" className="mt-1 font-display text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
        {s.name}
      </h1>
      <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <MapPin size={15} /> {[s.address, s.city, countryName(s.country)].filter(Boolean).join(", ")}
        </span>
        {s.emergency_number && (
          <span className="inline-flex items-center gap-1.5" data-testid="public-safety-emergency-number">
            <Phone size={15} /> Emergency: <strong>{s.emergency_number}</strong>
          </span>
        )}
      </div>

      {s.muster_point && (
        <p data-testid="public-safety-muster" className="mt-5 border-l-2 border-[#B3241B] bg-[#FFECEB] px-4 py-3 text-sm text-[#7A1712]">
          <strong>Muster point:</strong> {s.muster_point}. Go here and stay there until you are counted.
        </p>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="public-safety-risks">
        {[["Fire risk", s.fire_risk], ["Flood risk", s.flood_risk], ["Earthquake zone", s.earthquake_zone]].map(([label, v]) => (
          <div key={label} className="border border-border bg-white p-3">
            <div className="overline text-[0.6rem] text-slate-500">{label}</div>
            <div className="font-display mt-1 text-lg font-bold text-[#0F172A]">{risk(v)}</div>
          </div>
        ))}
      </div>

      {(s.sprinklers || s.hydrant_note) && (
        <p className="mt-4 inline-flex items-start gap-2 text-xs text-slate-600">
          <FirstAid size={15} className="mt-0.5 shrink-0 text-[#002FA7]" />
          {[s.sprinklers ? "Sprinklers fitted." : null, s.hydrant_note].filter(Boolean).join(" ")}
        </p>
      )}

      <h2 className="mt-9 text-base font-semibold text-[#0F172A] md:text-lg">What to do in an emergency</h2>
      <div className="mt-3 space-y-4" data-testid="public-safety-plans">
        {data.plans.length === 0 && <p className="text-sm text-slate-500">No procedures published yet.</p>}
        {data.plans.map((p, i) => (
          <div key={i} data-testid={`public-safety-plan-${p.hazard}`} className="border border-border bg-white p-4">
            <div className="flex items-center gap-2">
              <Warning size={16} className="text-[#B3241B]" />
              <span className="overline text-[0.6rem] text-[#B3241B]">{p.hazard_label}</span>
            </div>
            <div className="mt-1 text-sm font-semibold text-[#0F172A]">{p.title}</div>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-slate-700">
              {p.steps.map((step, j) => <li key={j}>{step}</li>)}
            </ol>
            {p.muster_point && <p className="mt-2 text-xs text-slate-500">Muster point: {p.muster_point}</p>}
          </div>
        ))}
      </div>

      {(s.warden_name || s.warden_phone) && (
        <p data-testid="public-safety-warden" className="mt-6 text-sm text-slate-600">
          Site warden: {[s.warden_name, s.warden_phone].filter(Boolean).join(" · ")}
        </p>
      )}

      <div className="mt-9 border-t border-border pt-5">
        <Link to="/login" className="text-sm font-semibold text-[#002FA7] hover:underline">
          Staff sign-in to report an incident
        </Link>
      </div>
    </div>
  );
}
